/**
 * How a spotlight arrives in the composer. `main`'s widget TYPED them -
 * a letter at a time, the finished line held, then the next - and the first
 * cut of this runtime swapped whole lines instead, which a customer noticed
 * (2026-09-29). Restored here, with `main`'s numbers.
 *
 * Like the reply's reveal (`./reveal`), the cadence is the embed contract's
 * and not one client's: the widget on a customer's page and the dashboard's
 * preview of it step through the same frames, so the preview cannot promise
 * a motion the page does not make.
 *
 * The frames are pure - a frame, the lines, what comes next - and
 * `playSpotlights` is the ONE loop that steps through them. It was two, one
 * in each client, until a browser check could not time either
 * (2026-09-29): a loop that exists once can be tested against a clock.
 */
export const SPOTLIGHT_TYPING = {
  /** One character. */
  typeMs: 40,
  /** The finished line, before the next begins. */
  holdMs: 2000,
  /** A whole line, for a visitor who asked for less motion. */
  swapMs: 3600,
} as const

export interface SpotlightFrame {
  /** Which line, counted without wrapping - the text wraps it. */
  line: number
  /** How many characters of it show. */
  chars: number
}

/** A line shown whole, whatever its length. */
const WHOLE = Number.MAX_SAFE_INTEGER

/** The lines worth showing: trimmed, the blank ones dropped. */
export function spotlightLines(lines: readonly string[]): string[] {
  return lines.map((line) => line.trim()).filter(Boolean)
}

/**
 * Where it starts: the first letter of the first line - or the whole line,
 * with motion reduced. One letter rather than none, so the composer is never
 * empty while it types.
 */
export function firstSpotlightFrame(reduced = false): SpotlightFrame {
  return { line: 0, chars: reduced ? WHOLE : 1 }
}

/**
 * What the frame shows. Counted in code points, not UTF-16 units: a letter
 * at a time must never show half an emoji.
 */
export function spotlightText(
  frame: SpotlightFrame,
  lines: readonly string[]
): string {
  if (lines.length === 0) return ""
  const line = lines[frame.line % lines.length]!
  if (frame.chars >= WHOLE) return line
  return Array.from(line).slice(0, frame.chars).join("")
}

/**
 * The frame after this one, and how long this one stays. A line still being
 * typed gains a letter; a finished one is held, then the next line begins -
 * the same line again when it is the only one, as `main` did. With motion
 * reduced, whole lines swap, and a lone line has nothing to swap to:
 * `afterMs` is Infinity and the client sets no timer.
 */
export function nextSpotlightFrame(
  frame: SpotlightFrame,
  lines: readonly string[],
  reduced = false
): { frame: SpotlightFrame; afterMs: number } {
  if (lines.length === 0) return { frame, afterMs: Infinity }
  if (reduced) {
    return {
      frame: { line: frame.line + 1, chars: WHOLE },
      afterMs: lines.length > 1 ? SPOTLIGHT_TYPING.swapMs : Infinity,
    }
  }
  const length = Array.from(lines[frame.line % lines.length]!).length
  if (frame.chars < length) {
    return {
      frame: { line: frame.line, chars: frame.chars + 1 },
      afterMs: SPOTLIGHT_TYPING.typeMs,
    }
  }
  return {
    frame: { line: frame.line + 1, chars: 1 },
    afterMs: SPOTLIGHT_TYPING.holdMs,
  }
}

/** The two timer calls the loop makes - the window's, or a test's. */
export interface SpotlightTimers {
  setTimeout(run: () => void, ms: number): unknown
  clearTimeout(id: never): void
}

/**
 * Type the lines, forever: `show` gets what the composer should say, now
 * and each time it changes. Returns the stop. With no lines it shows
 * nothing and sets no timer; with motion reduced and a lone line it shows
 * that line once and sets none.
 *
 * What to do with the text is the client's - the runtime writes an input's
 * placeholder, the dashboard's preview renders it - and so is knowing
 * whether the visitor asked for less motion.
 */
export function playSpotlights(
  lines: readonly string[],
  show: (text: string) => void,
  {
    reduced = false,
    timers = globalThis as unknown as SpotlightTimers,
  }: { reduced?: boolean; timers?: SpotlightTimers } = {}
): () => void {
  if (lines.length === 0) return () => {}
  let frame = firstSpotlightFrame(reduced)
  let timer: unknown
  let stopped = false
  const step = () => {
    if (stopped) return
    show(spotlightText(frame, lines))
    const next = nextSpotlightFrame(frame, lines, reduced)
    if (!Number.isFinite(next.afterMs)) return
    timer = timers.setTimeout(() => {
      frame = next.frame
      step()
    }, next.afterMs)
  }
  step()
  return () => {
    stopped = true
    timers.clearTimeout(timer as never)
  }
}
